import { useEffect, useState } from 'react'
import { continueVisit, fetchVisit, type VisitSummary } from '../lib/api'

/**
 * SPEC "Besuchs-Zusammenfassung" + "Wiedererkennung per Link": the personal
 * "Dein Besuch im VisionLab" page. Opened from the link K4ia mails out
 * (`send_summary`), so it must work with no session, no consent screen and
 * possibly weeks later.
 */
export function VisitPage({ token }: { token: string }) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ok'; visit: VisitSummary } | { status: 'gone' }
  >({ status: 'loading' })

  useEffect(() => {
    void fetchVisit(token)
      .then((r) => setState({ status: 'ok', visit: r.visit }))
      .catch(() => setState({ status: 'gone' }))
  }, [token])

  async function handleContinue() {
    await continueVisit(token)
    // Back to the chat shell, now attached to the original session.
    window.location.href = '/start'
  }

  if (state.status === 'loading') return null

  if (state.status === 'gone') {
    return (
      <main className="mx-auto max-w-lg p-6">
        <h1 className="mb-2 text-2xl font-semibold">Diese Zusammenfassung gibt es nicht mehr</h1>
        <p className="text-text-muted">
          Besuchsdaten werden nach der vereinbarten Frist automatisch gelöscht. Im J4F VisionLab
          kannst du jederzeit ein neues Gespräch mit K4ia starten.
        </p>
      </main>
    )
  }

  const { visit } = state
  const visitedOn = new Date(visit.startedAt).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })

  return (
    <main className="mx-auto max-w-lg p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Dein Besuch im J4F VisionLab</h1>
        <p className="text-sm text-text-muted">
          {visitedOn} · {visit.questionCount} {visit.questionCount === 1 ? 'Frage' : 'Fragen'} an
          K4ia
        </p>
      </header>

      <section className="mb-6">
        <h2 className="mb-2 font-medium">Das hast du dir angesehen</h2>
        {visit.showcases.length === 0 ? (
          <p className="text-sm text-text-muted">
            Für diesen Besuch ist noch kein Showcase vermerkt.
          </p>
        ) : (
          <ul className="space-y-3">
            {visit.showcases.map((s) => (
              <li key={s.id} className="rounded-xl border border-border p-4">
                <h3 className="font-medium">{s.title}</h3>
                <p className="mt-1 text-sm text-text-muted">{s.shortText}</p>
                {s.keyMessages.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm">
                    {s.keyMessages.map((m) => (
                      <li key={m} className="ml-4 list-disc">
                        {m}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {visit.questions.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 font-medium">Deine Fragen</h2>
          <ul className="space-y-1 text-sm text-text-muted">
            {visit.questions.map((q, i) => (
              <li key={`${i}-${q}`} className="ml-4 list-disc">
                {q}
              </li>
            ))}
          </ul>
        </section>
      )}

      <button onClick={() => void handleContinue()} className="btn-primary rounded-xl px-4 py-2">
        Gespräch mit K4ia fortsetzen
      </button>
    </main>
  )
}
